import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);
  const total = movies.length;

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  if (total === 0) return null;

  const goTo = (next: number) => setIndex((next + total) % total);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 44) {
      goTo(delta > 0 ? index - 1 : index + 1);
    }
  };

  const active = movies[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      aria-label="热门影视焦点推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[480px] w-full md:h-[560px]">
        {movies.map((movie, movieIndex) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              movieIndex === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={movieIndex !== index}
          >
            <img
              src={movie.backdrop || movie.poster}
              alt={`${movie.title} 焦点剧照`}
              title={`${movie.title} 大雷影视焦点推荐`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/75 to-primary-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/30" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-between px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-12">
          <div>
            <h1 className="text-sm font-medium tracking-[0.32em] text-accent-400">大雷影视大全</h1>
          </div>

          <div key={active.id} className="max-w-2xl animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-fill" />
                本周焦点
              </span>
              <span className="rounded-md border border-background-50/25 px-2.5 py-1 font-label text-xs tracking-wider text-background-100">
                {active.category === 'movie' ? '电影' : '剧集'} · {active.year}
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-background-50/10 px-2.5 py-1 text-xs text-background-100">
                <i className="ri-star-fill text-accent-400" />
                <span className="font-label text-sm font-semibold text-background-50">{active.rating.toFixed(1)}</span>
              </span>
            </div>

            <h2 className="text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
              {active.title}
            </h2>
            <p className="mt-1.5 font-label text-sm uppercase tracking-[0.2em] text-background-300/70">
              {active.subtitle}
            </p>

            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200/90 md:text-base">
              {active.summary}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-300/80">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-star-line text-accent-400" />
                导演 {active.director}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-map-pin-line text-accent-400" />
                {active.region}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-accent-400" />
                {active.duration}
              </span>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-play-fill text-base" />
                查看详情
              </Link>
              <Link
                to="/category/hot"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:border-background-50/60 hover:bg-background-50/10"
              >
                热门榜单
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          <div className="flex items-end justify-between gap-6">
            <div className="flex items-center gap-2">
              {movies.map((movie, movieIndex) => (
                <button
                  key={movie.id}
                  type="button"
                  onClick={() => goTo(movieIndex)}
                  aria-label={`切换到${movie.title}`}
                  aria-current={movieIndex === index}
                  className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                    movieIndex === index ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/30 hover:bg-background-50/60'
                  }`}
                />
              ))}
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <button
                type="button"
                onClick={() => goTo(index - 1)}
                aria-label="上一个焦点"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:border-accent-500 hover:text-accent-300"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <span className="font-label text-sm tracking-[0.2em] text-background-300/70">
                {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => goTo(index + 1)}
                aria-label="下一个焦点"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-100 transition-colors hover:border-accent-500 hover:text-accent-300"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}